import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";
import {
  OrderCreate,
  OrderUpdate,
  OrderResponse,
  OrderFilterRequest,
} from "../types/order";
import APIDailySub from "./daily/client";
import APIFileSub from "./file/client";
import APIOrderIdSub from "./orderId/client";

export default class APIOrders extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  daily = new APIDailySub(this._client);
  orderId = new APIOrderIdSub(this._client);
  file = new APIFileSub(this._client);

  // Create a new order
  create = async (orderData: OrderCreate): Promise<OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/`,
      method: "POST",
      body: orderData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get orders list with filtering
  list = async (filter?: OrderFilterRequest): Promise<OrderResponse[]> => {
    let params: any = {};
    if (filter) {
      if (filter.created_at_s) params.created_at_s = filter.created_at_s;
      if (filter.created_at_e) params.created_at_e = filter.created_at_e;
      if (filter.type) params.type = filter.type;
    }

    let res = await this._fetch({
      url: `/orders/`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get a single order by ID
  get = async (orderId: UUID): Promise<OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/${orderId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get a single order with flows loaded
  getWithFlows = async (orderId: UUID): Promise<OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/${orderId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update an order
  update = async (
    orderId: UUID,
    orderData: OrderUpdate,
  ): Promise<OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/${orderId}`,
      method: "PUT",
      body: orderData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Delete an order
  delete = async (orderId: UUID): Promise<void> => {
    await this._fetch({
      url: `/orders/${orderId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  // Import order from file using AI (keep this for the purchases page)
  aiImport = async ({
    partnerId,
    ...body
  }: {
    partnerId: UUID;
  } & types.BodyImportOrderFromFileOrdersAiImportPost): Promise<OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/ai_import`,
      method: "POST",
      params: { partner_id: partnerId },
      body: body,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create orders from pre-orders
  fromPreOrders = async (preOrderIds: string[]): Promise<OrderResponse[]> => {
    let res = await this._fetch({
      url: `/orders/from_pre_orders`,
      method: "POST",
      body: { pre_order_ids: preOrderIds },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Upload documents to an order
  uploadDocuments = async (
    orderId: UUID,
    files: File[],
  ): Promise<{
    message: string;
    files: Array<{ url: string; name: string }>;
    order_id: UUID;
  }> => {
    let res = await this._fetch({
      url: `/orders/${orderId}/documents/upload`,
      method: "POST",
      body: { files: files },
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get documents for an order
  getDocuments = async (
    orderId: UUID,
  ): Promise<{
    order_id: UUID;
    files: Array<{ url: string; name: string }>;
  }> => {
    let res = await this._fetch({
      url: `/orders/${orderId}/documents`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Download a document from an order
  downloadDocument = async (orderId: UUID, fileUrl: string): Promise<Blob> => {
    let res = await this._fetch({
      url: `/orders/${orderId}/documents/download`,
      method: "GET",
      params: { file_url: fileUrl },
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  // Remove a document from an order
  removeDocument = async (
    orderId: UUID,
    fileUrl: string,
  ): Promise<{
    message: string;
    removed_file: { url: string; name: string };
    order_id: UUID;
  }> => {
    let res = await this._fetch({
      url: `/orders/${orderId}/documents/remove`,
      method: "DELETE",
      params: { file_url: fileUrl },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create direct order (purchase or sale)
  postDirect = async (orderData: any): Promise<OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/direct`,
      method: "POST",
      body: orderData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Add flow to order
  addFlowToOrder = async (orderId: UUID, productId: UUID): Promise<any> => {
    let res = await this._fetch({
      url: `/orders/${orderId}/flows`,
      method: "POST",
      body: { product_id: productId },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Remove flow from order
  deleteFlowFromOrder = async (
    orderId: UUID,
    flowId: UUID,
  ): Promise<OrderResponse> => {
    let res = await this._fetch({
      url: `/orders/${orderId}/flows/${flowId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
